import {
  closePeriod,
  createPeriod,
  listCompanyKnowledge,
  listCompanySummaries,
  listPeriods,
  listProjectKnowledge,
  listProjectSummaries,
  reopenPeriod,
  type CoworkPeriod,
} from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useLanguage } from "@/context/language"
import { BackLabel } from "@/components/back-label"
import { useParams, useSearchParams } from "@solidjs/router"
import { useAppNavigate } from "@/cowork/workspace-route"
import { collectionPath } from "@/cowork/scope-url"
import {
  createEffect,
  createMemo,
  createResource,
  ErrorBoundary,
  For,
  Match,
  on,
  onCleanup,
  Show,
  Switch,
} from "solid-js"
import { createStore } from "solid-js/store"
import { CoworkFilesView } from "@/pages/cowork-files"
import { CoworkReportsView } from "@/pages/cowork-reports"
import { CoworkProjectsView } from "@/pages/cowork-projects"
import { CoworkEmployeesView } from "@/pages/cowork-employees"
import { CoworkCompanySettings } from "@/pages/cowork-company-settings"
import { CoworkKnowledgePage, knowledgeAvailableInScope } from "@/pages/cowork-knowledge"

const entitySections = ["overview", "projects", "files", "knowledge", "reports", "employees", "settings"] as const
const projectSections = ["overview", "files", "knowledge", "periods", "reports"] as const
type ScopeSection = (typeof entitySections)[number] | (typeof projectSections)[number]

export function CoworkEntityDetailPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const params = useParams<{ entityId: string; section?: string }>()
  const navigate = useAppNavigate()
  const [companies, companyActions] = createResource(listCompanySummaries)
  const company = createMemo(() =>
    companies.state === "ready" || companies.state === "refreshing"
      ? companies.latest?.find((item) => item.id === params.entityId)
      : undefined,
  )
  const section = createMemo<ScopeSection>(() => normalizeSection(params.section, entitySections))
  const [knowledge] = createResource(
    () => (section() === "overview" ? company()?.id : undefined),
    listCompanyKnowledge,
  )
  const knowledgeItems = createMemo(() =>
    knowledge.state === "ready"
      ? knowledge.latest?.filter((item) => item.company_id === company()?.id && item.project_id === null)
      : undefined,
  )
  const canManageEmployees = () => ["member", "admin", "owner"].includes(company()?.role ?? "")
  const visibleSections = () => entitySections.filter((item) => item !== "employees" || canManageEmployees())

  createEffect(
    on(
      () => company()?.id,
      (entityID) => {
        if (!entityID) return
        context.selectEntity(entityID)
        context.selectProject("")
      },
    ),
  )

  return (
    <Show
      when={company()?.id}
      keyed
      fallback={
        <div class="p-8 text-center">
          <p role={companies.error ? "alert" : "status"} class="text-[13px] text-v2-text-text-muted">
            {language.t(
              companies.loading
                ? "cowork.enterprise.entity.loading"
                : companies.error
                  ? "cowork.enterprise.entity.loadError"
                  : "cowork.enterprise.entity.notFound",
            )}
          </p>
          <Show when={!companies.loading}>
            <div class="mt-4 flex justify-center gap-4 text-[12px]">
              <button type="button" onClick={() => navigate("/companies")}>
                <BackLabel>{language.t("cowork.enterprise.entity.back")}</BackLabel>
              </button>
              <Show when={companies.error}>
                <button type="button" onClick={() => void companyActions.refetch()}>
                  {language.t("cowork.enterprise.scope.retry")}
                </button>
              </Show>
            </div>
          </Show>
        </div>
      }
    >
      {(entityID) => (
        <ScopeDetailLayout
          title={company()?.name ?? ""}
          status={company()?.status}
          section={section()}
          baseRoute={`/companies/${entityID}`}
          parent={{ label: language.t("cowork.enterprise.entity.back"), route: "/companies" }}
          onNavigate={navigate}
          sections={visibleSections()}
        >
          <Show when={section()} keyed>
            {(currentSection) => (
              <ErrorBoundary
                fallback={(_, reset) => (
                  <div role="alert" class="rounded-xl border border-v2-border-border-base p-6 text-[13px]">
                    <p>{language.t("cowork.enterprise.entity.loadError")}</p>
                    <button type="button" class="mt-3 font-medium" onClick={reset}>
                      {language.t("cowork.enterprise.scope.retry")}
                    </button>
                  </div>
                )}
              >
                <Switch>
                  <Match when={currentSection === "overview"}>
                    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                      <Metric label={language.t("cowork.projects.title")} value={company()?.projects ?? 0} />
                      <Metric label={language.t("cowork.navigation.files")} value={company()?.files ?? 0} />
                      <Metric label={language.t("cowork.enterprise.nav.reports")} value={company()?.reports ?? 0} />
                      <Metric label={language.t("cowork.usage.workflows")} value={company()?.active_workflows ?? 0} />
                      <Metric
                        label={language.t("cowork.enterprise.knowledge.title")}
                        value={
                          knowledgeItems()?.length ??
                          language.t(knowledge.loading ? "common.loading" : "cowork.enterprise.common.notAvailable")
                        }
                      />
                    </div>
                    <div class="mt-5">
                      <DetailPanel title={language.t("cowork.enterprise.entity.details")}>
                        <dl class="grid gap-5 p-5 sm:grid-cols-2">
                          <For
                            each={[
                              { label: language.t("cowork.enterprise.entity.company"), value: company()?.name },
                              { label: language.t("cowork.enterprise.companies.country"), value: company()?.country },
                              {
                                label: language.t("cowork.enterprise.companies.registrationNumber"),
                                value: company()?.registration_number,
                              },
                              {
                                label: language.t("cowork.enterprise.companies.vatNumber"),
                                value: company()?.vat_number,
                              },
                              {
                                label: language.t("cowork.enterprise.companies.lastActivity"),
                                value: activityDate(company()?.last_activity ?? ""),
                              },
                            ]}
                          >
                            {(item) => (
                              <div>
                                <dt class="text-[11px] text-v2-text-text-muted">{item.label}</dt>
                                <dd class="mt-1 break-words text-[13px]">
                                  {item.value || language.t("cowork.enterprise.common.notAvailable")}
                                </dd>
                              </div>
                            )}
                          </For>
                        </dl>
                      </DetailPanel>
                    </div>
                  </Match>
                  <Match when={currentSection === "projects"}>
                    <CoworkProjectsView entityID={entityID} embedded onChanged={() => void companyActions.refetch()} />
                  </Match>
                  <Match when={currentSection === "files"}>
                    <CoworkFilesView entityID={entityID} embedded navigateFolder={navigate} />
                  </Match>
                  <Match when={currentSection === "knowledge"}>
                    <CoworkKnowledgePage
                      companyID={entityID}
                      canEdit={["member", "admin", "owner"].includes(company()?.role ?? "")}
                    />
                  </Match>
                  <Match when={currentSection === "reports"}>
                    <CoworkReportsView entityID={entityID} embedded />
                  </Match>
                  <Match when={currentSection === "employees"}>
                    <CoworkEmployeesView companyID={entityID} canManage={canManageEmployees()} />
                  </Match>
                  <Match when={currentSection === "settings"}>
                    <CoworkCompanySettings
                      companyID={entityID}
                      country={company()?.country ?? undefined}
                      canEdit={["admin", "owner"].includes(company()?.role ?? "")}
                    />
                  </Match>
                </Switch>
              </ErrorBoundary>
            )}
          </Show>
        </ScopeDetailLayout>
      )}
    </Show>
  )
}

export function CoworkProjectDetailPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const params = useParams<{ projectId: string; section?: string }>()
  const [query] = useSearchParams<{ period?: string }>()
  const routeScope = createMemo(() => ({ projectID: params.projectId }))
  const navigate = useAppNavigate()
  const currentYear = new Date().getFullYear()
  let mounted = true
  onCleanup(() => {
    mounted = false
  })
  const [projects, projectActions] = createResource(
    () => params.projectId,
    () => listProjectSummaries(),
  )
  const initialPeriodForm = {
    open: false,
    name: "",
    granularity: "month" as CoworkPeriod["granularity"],
    period: "january",
    day: 1,
    year: currentYear,
    startDate: "",
    endDate: "",
    saving: false,
    error: "",
    updateError: "",
  }
  const [periodForm, setPeriodForm] = createStore({ ...initialPeriodForm })
  createEffect(on(routeScope, () => setPeriodForm({ ...initialPeriodForm })))
  const project = createMemo(() =>
    projects.state === "ready" || projects.state === "refreshing"
      ? projects.latest?.find((item) => item.id === params.projectId)
      : undefined,
  )
  const canEditProject = createMemo(() => project()?.project_role === "owner" || project()?.project_role === "editor")
  const section = createMemo<ScopeSection>(() => normalizeSection(params.section, projectSections))
  const [periods, periodActions] = createResource(
    () => {
      const value = project()
      return value ? { projectID: value.id, companyID: value.company_id } : undefined
    },
    ({ projectID, companyID }) => listPeriods(projectID, companyID),
  )
  // Only settled reads are safe under the route's outer Suspense, including on errors.
  const periodItems = createMemo(() => (periods.state === "ready" ? periods.latest : undefined))
  const selectedPeriodID = createMemo(() => {
    const value = project()
    if (!value || context.state.entityId !== value.company_id || context.state.projectId !== value.id) return undefined
    return periodItems()?.find(
      (period) =>
        period.id === context.state.periodId &&
        period.project_id === value.id &&
        period.company_id === value.company_id,
    )?.id
  })
  const periodFilterID = () => query.period || undefined
  const [knowledge] = createResource(
    () => {
      const value = project()
      return value && section() === "overview"
        ? { projectID: value.id, companyID: value.company_id }
        : undefined
    },
    ({ projectID, companyID }) => listProjectKnowledge(projectID, companyID),
  )
  const knowledgeItems = createMemo(() =>
    knowledge.state === "ready"
      ? knowledge.latest?.filter((item) => {
          const selected = project()
          return selected && knowledgeAvailableInScope(item, { companyID: selected.company_id, projectID: selected.id })
        })
      : undefined,
  )

  createEffect(
    on([() => project()?.id, () => project()?.company_id], ([projectID, companyID]) => {
      if (!projectID || !companyID) return
      context.selectEntity(companyID)
      context.selectProject(projectID)
    }),
  )

  const savePeriod = async (event: SubmitEvent) => {
    event.preventDefault()
    const startedScope = routeScope()
    const target = project()
    const values = periodCreationValues(periodForm, language.t)
    if (!target || !canEditProject() || periodForm.saving || !values.name) return
    setPeriodForm({ saving: true, error: "" })
    try {
      const created = await createPeriod(
        target.id,
        {
          name: values.name,
          granularity: periodForm.granularity,
          start_date: values.startDate,
          end_date: values.endDate,
          fiscal_year: periodForm.granularity === "custom" ? undefined : periodForm.year,
          is_current: true,
        },
        target.company_id,
      )
      if (!mounted || routeScope() !== startedScope) return
      await Promise.all([
        periodActions.refetch(),
        context.state.entityId === target.company_id && context.state.projectId === target.id
          ? context.refreshPeriods()
          : undefined,
      ])
      if (!mounted || routeScope() !== startedScope) return
      if (context.state.entityId === target.company_id && context.state.projectId === target.id) {
        navigate(collectionPath("periods", { projectID: target.id, periodID: created.id }))
      }
      setPeriodForm({
        open: false,
        name: "",
        granularity: "month",
        period: "january",
        day: 1,
        year: currentYear,
        startDate: "",
        endDate: "",
        saving: false,
        error: "",
      })
    } catch {
      if (mounted && routeScope() === startedScope) setPeriodForm("error", language.t("common.requestFailed"))
    } finally {
      if (mounted && routeScope() === startedScope) setPeriodForm("saving", false)
    }
  }

  const setPeriodGranularity = (granularity: CoworkPeriod["granularity"]) => {
    setPeriodForm({
      granularity,
      period: periodOptions(granularity)[0] ?? "january",
      day: 1,
    })
  }

  const togglePeriodClosed = async (value: CoworkPeriod) => {
    const startedScope = routeScope()
    const target = project()
    if (!target || !canEditProject() || value.project_id !== target.id || value.company_id !== target.company_id) return
    setPeriodForm("updateError", "")
    try {
      if (value.is_locked || value.period_status === "closed")
        await reopenPeriod(target.id, value.id, target.company_id)
      else await closePeriod(target.id, value.id, target.company_id)
      if (!mounted || routeScope() !== startedScope) return
      await Promise.all([
        periodActions.refetch(),
        context.state.entityId === target.company_id && context.state.projectId === target.id
          ? context.refreshPeriods()
          : undefined,
      ])
    } catch {
      if (mounted && routeScope() === startedScope) setPeriodForm("updateError", language.t("common.requestFailed"))
    }
  }

  return (
    <Show
      when={project()?.id}
      fallback={
        <div class="p-8 text-center">
          <p role={projects.error ? "alert" : "status"} class="text-[13px] text-v2-text-text-muted">
            {language.t(
              projects.loading
                ? "cowork.enterprise.project.loading"
                : projects.error
                  ? "cowork.enterprise.project.loadError"
                  : "cowork.enterprise.project.notFound",
            )}
          </p>
          <Show when={!projects.loading}>
            <div class="mt-4 flex justify-center gap-4 text-[12px]">
              <button type="button" onClick={() => navigate("/projects")}>
                <BackLabel>{language.t("cowork.enterprise.project.back")}</BackLabel>
              </button>
              <button type="button" onClick={() => void projectActions.refetch()}>
                {language.t("cowork.enterprise.scope.retry")}
              </button>
            </div>
          </Show>
        </div>
      }
    >
      <ScopeDetailLayout
        title={project()?.name ?? ""}
        status={project()?.status}
        section={section()}
        baseRoute={`/projects/${params.projectId}`}
        parent={
          project()
            ? { label: project()!.company_name, route: `/companies/${project()!.company_id}/projects` }
            : undefined
        }
        onNavigate={navigate}
        sections={projectSections}
      >
        <Switch>
          <Match when={section() === "overview"}>
            <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <Metric label={language.t("cowork.navigation.files")} value={project()?.files ?? 0} />
              <Metric label={language.t("cowork.enterprise.nav.reports")} value={project()?.reports ?? 0} />
              <Metric label={language.t("cowork.usage.workflows")} value={project()?.workflows ?? 0} />
              <Metric
                label={language.t("cowork.enterprise.scope.tab.periods")}
                value={
                  periodItems()?.length ??
                  language.t(periods.loading ? "common.loading" : "cowork.enterprise.common.notAvailable")
                }
              />
              <Metric
                label={language.t("cowork.enterprise.knowledge.title")}
                value={
                  knowledgeItems()?.length ??
                  language.t(knowledge.loading ? "common.loading" : "cowork.enterprise.common.notAvailable")
                }
              />
            </div>
          </Match>
          <Match when={section() === "files"}>
            <CoworkFilesView
              entityID={project()?.company_id ?? ""}
              projectID={params.projectId}
              periodID={periodFilterID()}
              embedded
              navigateFolder={navigate}
            />
          </Match>
          <Match when={section() === "knowledge"}>
            <CoworkKnowledgePage
              projectID={params.projectId}
              companyID={project()?.company_id ?? ""}
              canEdit={canEditProject()}
            />
          </Match>
          <Match when={section() === "periods"}>
            <section class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
              <div class="flex flex-col gap-4 border-b border-v2-border-border-base px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 class="text-[16px] font-semibold">{language.t("cowork.enterprise.period.title")}</h2>
                  <p class="mt-1 text-[11px] text-v2-text-text-muted">
                    {language.t("cowork.enterprise.period.description")}
                  </p>
                </div>
                <Show when={canEditProject()}>
                  <button
                    type="button"
                    class="rounded-lg bg-v2-background-bg-contrast px-3 py-2 text-[11px] font-semibold text-v2-text-text-contrast"
                    onClick={() => setPeriodForm({ open: true, error: "" })}
                  >
                    {language.t("cowork.enterprise.period.new")}
                  </button>
                </Show>
              </div>
              <Show when={periodForm.updateError}>
                <p role="alert" class="px-5 py-4 text-[11px] text-v2-state-fg-danger">
                  {periodForm.updateError}
                </p>
              </Show>
              <Show when={!periods.error} fallback={<PanelError onRetry={() => void periodActions.refetch()} />}>
                <Show
                  when={(periodItems()?.length ?? 0) > 0}
                  fallback={
                    <PanelEmpty loading={periods.loading} text={language.t("cowork.enterprise.period.empty")} />
                  }
                >
                  <div class="divide-y divide-v2-border-border-muted">
                    <For each={periodItems() ?? []}>
                      {(period) => (
                        <div
                          class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                          classList={{ "bg-v2-background-bg-layer-01": selectedPeriodID() === period.id }}
                        >
                          <button
                            type="button"
                            class="min-w-0 text-left"
                             onClick={() => navigate(collectionPath("periods", { projectID: params.projectId, periodID: period.id }))}
                          >
                            <div class="flex flex-wrap items-center gap-2">
                              <span class="text-[13px] font-semibold">{period.name}</span>
                              <PeriodStatus status={period.period_status} />
                              <Show when={selectedPeriodID() === period.id}>
                                <span class="rounded-md bg-v2-state-bg-success px-2 py-1 text-[10px] font-medium text-v2-state-fg-success">
                                  {language.t("cowork.enterprise.period.current")}
                                </span>
                              </Show>
                            </div>
                            <div class="mt-1 text-[10px] text-v2-text-text-muted">
                              {language.t(
                                `cowork.enterprise.period.granularity.${period.granularity}` as keyof typeof import("@/i18n/en").dict,
                              )}
                              {periodRange(period.start_date, period.end_date)
                                ? ` · ${periodRange(period.start_date, period.end_date)}`
                                : ""}
                            </div>
                          </button>
                          <Show when={canEditProject()}>
                            <button
                              type="button"
                              class="self-start rounded-lg border border-v2-border-border-base bg-v2-background-bg-button-neutral px-3 py-2 text-[11px] font-medium hover:bg-v2-overlay-simple-overlay-hover sm:self-auto"
                              onClick={() => void togglePeriodClosed(period)}
                            >
                              {period.is_locked || period.period_status === "closed"
                                ? language.t("cowork.enterprise.period.reopen")
                                : language.t("cowork.enterprise.period.close")}
                            </button>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </section>
            <Show when={periodForm.open}>
              <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation">
                <section
                  class="w-[min(92vw,560px)] overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base shadow-2xl"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="create-period-title"
                >
                  <form onSubmit={savePeriod}>
                    <header class="px-6 py-5">
                      <h2 id="create-period-title" class="text-[18px] font-semibold">
                        {language.t("cowork.enterprise.period.add")}
                      </h2>
                    </header>
                    <div class="grid gap-5 border-t border-v2-border-border-base px-6 py-5">
                      <label class="grid gap-2 text-[12px] font-medium">
                        {language.t("cowork.period.type")}
                        <select
                          class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px]"
                          value={periodForm.granularity}
                          onChange={(event) =>
                            setPeriodGranularity(event.currentTarget.value as CoworkPeriod["granularity"])
                          }
                        >
                          <option value="month">{language.t("cowork.enterprise.period.granularity.month")}</option>
                          <option value="quarter">{language.t("cowork.enterprise.period.granularity.quarter")}</option>
                          <option value="year">{language.t("cowork.enterprise.period.granularity.year")}</option>
                          <option value="day">{language.t("cowork.enterprise.period.granularity.day")}</option>
                          <option value="custom">{language.t("cowork.enterprise.period.granularity.custom")}</option>
                        </select>
                      </label>
                      <Show when={["month", "quarter", "day"].includes(periodForm.granularity)}>
                        <label class="grid gap-2 text-[12px] font-medium">
                          {language.t("cowork.period.select")}
                          <select
                            class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px]"
                            value={periodForm.period}
                            onChange={(event) => setPeriodForm("period", event.currentTarget.value)}
                          >
                            <For each={periodOptions(periodForm.granularity)}>
                              {(option) => (
                                <option value={option}>
                                  {periodOptionLabel(periodForm.granularity, option, language.t)}
                                </option>
                              )}
                            </For>
                          </select>
                        </label>
                      </Show>
                      <Show when={periodForm.granularity === "day"}>
                        <label class="grid gap-2 text-[12px] font-medium">
                          {language.t("cowork.enterprise.period.day")}
                          <select
                            class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px]"
                            value={periodForm.day}
                            onChange={(event) => setPeriodForm("day", Number(event.currentTarget.value))}
                          >
                            <For each={periodDays(periodForm.period, periodForm.year)}>
                              {(day) => <option value={day}>{day}</option>}
                            </For>
                          </select>
                        </label>
                      </Show>
                      <Show when={periodForm.granularity !== "custom"}>
                        <label class="grid gap-2 text-[12px] font-medium">
                          {language.t("cowork.period.year")}
                          <select
                            class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px]"
                            value={periodForm.year}
                            onChange={(event) => setPeriodForm("year", Number(event.currentTarget.value))}
                          >
                            <For each={periodYears(currentYear)}>{(year) => <option value={year}>{year}</option>}</For>
                          </select>
                        </label>
                      </Show>
                      <Show when={periodForm.granularity === "custom"}>
                        <label class="grid gap-2 text-[12px] font-medium">
                          {language.t("cowork.enterprise.period.name")}
                          <input
                            required
                            class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px] outline-none"
                            placeholder={language.t("cowork.enterprise.period.namePlaceholder")}
                            value={periodForm.name}
                            onInput={(event) => setPeriodForm("name", event.currentTarget.value)}
                          />
                        </label>
                        <div class="grid gap-4 sm:grid-cols-2">
                          <label class="grid gap-2 text-[12px] font-medium">
                            {language.t("cowork.enterprise.period.from")}
                            <input
                              type="date"
                              required
                              class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px]"
                              value={periodForm.startDate}
                              onInput={(event) => setPeriodForm("startDate", event.currentTarget.value)}
                            />
                          </label>
                          <label class="grid gap-2 text-[12px] font-medium">
                            {language.t("cowork.enterprise.period.to")}
                            <input
                              type="date"
                              required
                              class="h-11 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[13px]"
                              value={periodForm.endDate}
                              onInput={(event) => setPeriodForm("endDate", event.currentTarget.value)}
                            />
                          </label>
                        </div>
                      </Show>
                    </div>
                    <Show when={periodForm.error}>
                      <p class="mx-6 mb-4 rounded-lg bg-v2-state-bg-danger px-3 py-2 text-[11px] text-v2-state-fg-danger">
                        {periodForm.error}
                      </p>
                    </Show>
                    <footer class="flex justify-end gap-2 border-t border-v2-border-border-base px-6 py-4">
                      <button
                        type="button"
                        class="rounded-lg border border-v2-border-border-base px-4 py-2 text-[12px] font-medium"
                        onClick={() => setPeriodForm("open", false)}
                      >
                        {language.t("common.cancel")}
                      </button>
                      <button
                        type="submit"
                        disabled={periodForm.saving}
                        class="rounded-lg bg-v2-background-bg-contrast px-4 py-2 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                      >
                        {periodForm.saving
                          ? language.t("cowork.enterprise.period.creating")
                          : language.t("cowork.enterprise.period.add")}
                      </button>
                    </footer>
                  </form>
                </section>
              </div>
            </Show>
          </Match>
          <Match when={section() === "reports"}>
            <CoworkReportsView
              entityID={project()?.company_id ?? ""}
              projectID={params.projectId}
              periodID={periodFilterID()}
              embedded
            />
          </Match>
          <Match when={true}>
            <SectionPlaceholder section={section()} scope={project()?.name ?? ""} />
          </Match>
        </Switch>
      </ScopeDetailLayout>
    </Show>
  )
}

function ScopeDetailLayout(props: {
  title: string
  status?: string
  section: ScopeSection
  baseRoute: string
  parent?: { label: string; route: string }
  sections: readonly ScopeSection[]
  onNavigate: (route: string) => void
  children: import("solid-js").JSX.Element
}) {
  const language = useLanguage()
  return (
    <div class="min-h-full bg-v2-background-bg-deep">
      <div class="border-b border-v2-border-border-base bg-v2-background-bg-base px-4 pt-7 sm:px-7 xl:px-10">
        <div class="mx-auto max-w-[1500px]">
          <Show when={props.parent}>
            {(parent) => (
              <button
                type="button"
                class="mb-4 text-[12px] text-v2-text-text-muted hover:text-v2-text-text-base"
                onClick={() => props.onNavigate(parent().route)}
              >
                <BackLabel>{parent().label}</BackLabel>
              </button>
            )}
          </Show>
          <div class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div class="flex flex-wrap items-center gap-3">
                <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{props.title}</h1>
                <Show when={props.status}>
                  <span class="rounded-md bg-v2-state-bg-success px-2 py-1 text-[10px] font-medium capitalize text-v2-state-fg-success">
                    {props.status}
                  </span>
                </Show>
              </div>
            </div>
          </div>
          <nav class="mt-7 flex gap-1 overflow-x-auto" aria-label={language.t("cowork.enterprise.scope.navigation")}>
            <For each={props.sections}>
              {(item) => (
                <button
                  type="button"
                  class="whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-[12px] font-medium text-v2-text-text-muted hover:text-v2-text-text-base"
                  classList={{ "border-v2-border-border-strong text-v2-text-text-base": props.section === item }}
                  aria-current={props.section === item ? "page" : undefined}
                  onClick={() => props.onNavigate(`${props.baseRoute}/${item}`)}
                >
                  {language.t(`cowork.enterprise.scope.tab.${item}` as keyof typeof import("@/i18n/en").dict)}
                </button>
              )}
            </For>
          </nav>
        </div>
      </div>
      <div class="mx-auto max-w-[1500px] px-4 py-6 sm:px-7 xl:px-10">{props.children}</div>
    </div>
  )
}

function Metric(props: { label: string; value: number | string; suffix?: string }) {
  return (
    <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base px-5 py-4">
      <div class="text-[11px] text-v2-text-text-muted">{props.label}</div>
      <div class="mt-1 text-[23px] font-semibold tabular-nums">
        {props.value}
        <Show when={props.suffix}>
          <span class="ml-1 text-[10px] font-normal text-v2-text-text-muted">{props.suffix}</span>
        </Show>
      </div>
    </div>
  )
}

function DetailPanel(props: { title: string; children: import("solid-js").JSX.Element }) {
  return (
    <section class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
      <h2 class="border-b border-v2-border-border-base px-5 py-4 text-[14px] font-semibold">{props.title}</h2>
      {props.children}
    </section>
  )
}

function PanelEmpty(props: { loading: boolean; text: string }) {
  const language = useLanguage()
  return (
    <div class="grid min-h-36 place-items-center p-6 text-center text-[11px] text-v2-text-text-muted">
      {props.loading ? (
        <span
          role="status"
          aria-label={language.t("common.loading")}
          class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base"
        />
      ) : (
        props.text
      )}
    </div>
  )
}

function PanelError(props: { onRetry: () => void }) {
  const language = useLanguage()
  return (
    <div role="alert" class="p-6 text-center text-[11px] text-v2-text-text-muted">
      <p>{language.t("cowork.enterprise.scope.loadError")}</p>
      <button type="button" class="mt-3 font-medium" onClick={props.onRetry}>
        {language.t("cowork.enterprise.scope.retry")}
      </button>
    </div>
  )
}

function SectionPlaceholder(props: { section: ScopeSection; scope: string }) {
  const language = useLanguage()
  return (
    <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-base px-6 py-16 text-center">
      <h2 class="text-[16px] font-semibold">
        {language.t(`cowork.enterprise.scope.tab.${props.section}` as keyof typeof import("@/i18n/en").dict)}
      </h2>
      <p class="mx-auto mt-2 max-w-md text-[12px] text-v2-text-text-muted">
        {language.t("cowork.enterprise.scope.sectionDescription", {
          section: language.t(`cowork.enterprise.scope.tab.${props.section}` as keyof typeof import("@/i18n/en").dict),
          scope: props.scope,
        })}
      </p>
    </div>
  )
}

function normalizeSection(value: string | undefined, available: readonly ScopeSection[]): ScopeSection {
  return available.includes(value as ScopeSection) ? (value as ScopeSection) : "overview"
}

function PeriodStatus(props: { status: string }) {
  const language = useLanguage()
  const label = () => {
    const keys: Record<string, keyof typeof import("@/i18n/en").dict> = {
      open: "cowork.enterprise.period.status.open",
      processing: "cowork.enterprise.period.status.processing",
      needs_review: "cowork.enterprise.period.status.needsReview",
      closing: "cowork.enterprise.period.status.closing",
      closed: "cowork.enterprise.period.status.closed",
      locked: "cowork.enterprise.period.status.locked",
    }
    return language.t(keys[props.status] ?? "cowork.enterprise.period.status.open")
  }
  return (
    <span
      class="rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium capitalize"
      classList={{
        "bg-v2-state-bg-success text-v2-state-fg-success": props.status === "open",
        "bg-v2-state-bg-warning text-v2-state-fg-warning": ["processing", "needs_review", "closing"].includes(
          props.status,
        ),
        "text-v2-text-text-muted": ["closed", "locked"].includes(props.status),
      }}
    >
      {label()}
    </span>
  )
}

function periodRange(start?: string | null, end?: string | null) {
  if (!start && !end) return ""
  const format = (value: string) =>
    new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(
      new Date(`${value}T00:00:00`),
    )
  if (start && end) return `${format(start)} – ${format(end)}`
  return format((start || end)!)
}

function periodOptions(granularity: CoworkPeriod["granularity"]) {
  if (granularity === "quarter") return ["q1", "q2", "q3", "q4"]
  if (granularity === "month" || granularity === "day") {
    return [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ]
  }
  return []
}

function periodYears(currentYear: number) {
  return [currentYear - 1, currentYear, currentYear + 1]
}

function periodOptionLabel(
  granularity: CoworkPeriod["granularity"],
  period: string,
  translate: (key: string) => string,
) {
  if (granularity === "quarter") return translate(`cowork.period.${period}`)
  return translate(`cowork.period.month.${period}`)
}

function periodDays(month: string, year: number) {
  const monthIndex = Math.max(0, periodOptions("month").indexOf(month))
  return Array.from({ length: new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate() }, (_, index) => index + 1)
}

function periodCreationValues(
  form: {
    name: string
    granularity: CoworkPeriod["granularity"]
    period: string
    day: number
    year: number
    startDate: string
    endDate: string
  },
  translate: (key: string) => string,
) {
  if (form.granularity === "custom") {
    return { name: form.name.trim(), startDate: form.startDate || undefined, endDate: form.endDate || undefined }
  }
  const monthIndex = Math.max(0, periodOptions("month").indexOf(form.period))
  if (form.granularity === "year") {
    return {
      name: String(form.year),
      startDate: formatPeriodDate(form.year, 0, 1),
      endDate: formatPeriodDate(form.year, 12, 0),
    }
  }
  if (form.granularity === "quarter") {
    const quarter = Math.max(1, Number(form.period.slice(1)) || 1)
    const startMonth = quarter * 3 - 3
    return {
      name: `${periodOptionLabel("quarter", form.period, translate)} ${form.year}`,
      startDate: formatPeriodDate(form.year, startMonth, 1),
      endDate: formatPeriodDate(form.year, startMonth + 3, 0),
    }
  }
  if (form.granularity === "day") {
    const day = Math.min(form.day, periodDays(form.period, form.year).length)
    const value = formatPeriodDate(form.year, monthIndex, day)
    const name = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "long", year: "numeric" }).format(
      new Date(`${value}T00:00:00`),
    )
    return { name, startDate: value, endDate: value }
  }
  return {
    name: `${periodOptionLabel("month", form.period, translate)} ${form.year}`,
    startDate: formatPeriodDate(form.year, monthIndex, 1),
    endDate: formatPeriodDate(form.year, monthIndex + 1, 0),
  }
}

function formatPeriodDate(year: number, month: number, day: number) {
  const date = new Date(Date.UTC(year, month, day))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`
}

function activityDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(
        date,
      )
    : ""
}
